import compact from 'lodash/compact'
import {
  IIssueAutomationRuleActionEnum,
  IIssueAutomationRuleNodeInput,
  IIssueAutomationRuleNodeShipmentEventFieldTypeEnum,
  IIssueAutomationRuleNodeShipmentEventOperatorTypeEnum,
  IIssueAutomationRuleNodeShipmentInformationFieldTypeEnum,
  IIssueAutomationRuleNodeShipmentInformationOperatorTypeEnum,
  IIssueAutomationRuleNodeTimeWindowFieldTypeEnum,
  IIssueAutomationRuleNodeTimeWindowOperatorTypeEnum,
  IIssueAutomationRuleNodeTypeEnum,
  IShipmentStates,
} from 'types/graphqlTypes'
import { ShipmentIssueCategory, ShipmentIssueType } from 'apps/marketplace/apiTypes/shipmentIssues'
import filterIssueTypes from '../control-panel/utils/filterIssueTypes'
import { IRuleNode, TIssueRuleFormFormSchema } from './components/RuleForm'
import {
  TWENTY_FOUR_HOURS_IN_SECONDS,
  ONE_HOUR_IN_SECONDS,
  TIME_WINDOW_AMOUNT_OPTIONS_DAYS,
  TIME_WINDOW_AMOUNT_OPTIONS_HOURS,
  TIME_WINDOW_AMOUNT_OPTIONS_MINUTES,
  TIME_WINDOW_FIELD_OPTIONS,
  TIME_WINDOW_OPERATOR_OPTIONS,
  SHIPMENT_STATUS_OPTIONS,
  SHIPMENT_INFORMATION_FIELD_OPTIONS_M5,
  SHIPMENT_EVENT_ON_HOLD_AND_TENDERED_STATUS_MESSAGE,
  SHIPMENT_EVENT_ON_HOLD_STATUS_MESSAGE,
  SHIPMENT_EVENT_TENDERED_STATUS_MESSAGE,
} from './constants'
import { IIssueAutomationRuleFragment } from './graphql/IssueAutomationRuleFragment'

export const getIssueType = (
  id: string,
  types: ShipmentIssueType[],
  categories: ShipmentIssueCategory[]
): ShipmentIssueType => {
  const issueTypes = filterIssueTypes(types, '', categories)
  const [issueType] = issueTypes.filter(item => item.id.toString() === id)
  return issueType
}

export const getDefaultSelectedNodeTypes = (selectedRule: IIssueAutomationRuleFragment | null) => {
  const defaultTypes: IIssueAutomationRuleNodeTypeEnum[] = []

  if (!selectedRule) {
    return defaultTypes
  }

  selectedRule.issueAutomationRuleNodes.forEach(ruleNode => {
    if (ruleNode.parameters.__typename === 'IssueAutomationRuleNodeShipmentStateParameters') {
      defaultTypes.push(IIssueAutomationRuleNodeTypeEnum.shipment_status)
    } else if (ruleNode.parameters.__typename === 'IssueAutomationRuleNodeTimeWindowParameters') {
      defaultTypes.push(IIssueAutomationRuleNodeTypeEnum.time_window)
    } else if (
      ruleNode.parameters.__typename === 'IssueAutomationRuleNodeShipmentInformationParameters'
    ) {
      defaultTypes.push(IIssueAutomationRuleNodeTypeEnum.shipment_information)
    }
  })

  return defaultTypes
}

export const getUnitFromSeconds = (seconds: number) => {
  if (seconds % TWENTY_FOUR_HOURS_IN_SECONDS === 0) {
    return 'days'
  }
  if (seconds >= ONE_HOUR_IN_SECONDS) {
    return 'hours'
  }
  return 'minutes'
}

export const getTimeAmountFromSeconds = (seconds: number, unit: string) => {
  if (unit === 'days') {
    return Math.floor(seconds / TWENTY_FOUR_HOURS_IN_SECONDS)
  }
  if (unit === 'hours') {
    return seconds / ONE_HOUR_IN_SECONDS
  }
  return Math.floor(seconds / 60)
}

export const getTimeAmountInSeconds = (timeAmount: number, unit: string) => {
  if (unit === 'days') {
    return timeAmount * TWENTY_FOUR_HOURS_IN_SECONDS
  }
  if (unit === 'hours') {
    return timeAmount * ONE_HOUR_IN_SECONDS
  }
  return timeAmount * 60
}

export const getTimeWindowOptionsByUnit = (timeWindowUnit: string) => {
  if (timeWindowUnit === 'minutes') return TIME_WINDOW_AMOUNT_OPTIONS_MINUTES
  if (timeWindowUnit === 'hours') return TIME_WINDOW_AMOUNT_OPTIONS_HOURS
  if (timeWindowUnit === 'days') return TIME_WINDOW_AMOUNT_OPTIONS_DAYS

  return TIME_WINDOW_AMOUNT_OPTIONS_MINUTES
}

const getSortPosition = (node: IRuleNode) => {
  if (node.isShipmentStatus) {
    return 0
  }
  if (node.isTimeWindow) {
    return 1
  }
  if (node.isShipmentInformation) {
    return 2
  }
  if (node.isShipmentEvent) {
    return 3
  }
  return -1
}

export const getDefaultRuleNodesValues = (selectedRule: IIssueAutomationRuleFragment | null) => {
  if (!selectedRule) {
    return {
      nodes: [],
    }
  }

  const defaultNodes: IRuleNode[] = []

  selectedRule.issueAutomationRuleNodes.forEach(ruleNode => {
    if (ruleNode.parameters.__typename === 'IssueAutomationRuleNodeShipmentStateParameters') {
      defaultNodes.push({
        shipmentStatusOperator: 'is-any',
        shipmentStatusFields: ruleNode.parameters.shipmentStates,
        isShipmentStatus: true,
      })
    } else if (ruleNode.parameters.__typename === 'IssueAutomationRuleNodeTimeWindowParameters') {
      const unit = getUnitFromSeconds(ruleNode.parameters.timeWindowSeconds)
      const timeAmount = getTimeAmountFromSeconds(ruleNode.parameters.timeWindowSeconds, unit)
      defaultNodes.push({
        timeWindowField: ruleNode.parameters.timeWindowField,
        timeWindowTimeAmount: timeAmount,
        timeWindowUnit: unit,
        timeWindowOperator: ruleNode.parameters.timeWindowOperator,
        isTimeWindow: true,
      })
    } else if (
      ruleNode.parameters.__typename === 'IssueAutomationRuleNodeShipmentInformationParameters'
    ) {
      defaultNodes.push({
        shipmentInformationOperator: ruleNode.parameters.shipmentInformationOperator,
        shipmentInformationFields: ruleNode.parameters.shipmentInformationFields,
        isShipmentInformation: true,
      })
    } else if (
      ruleNode.parameters.__typename === 'IssueAutomationRuleNodeShipmentEventParameters'
    ) {
      defaultNodes.push({
        shipmentEventField: ruleNode.parameters.shipmentEventFields[0], // We only have one available field for now, update if we eventually add more
        shipmentEventOperator: ruleNode.parameters.shipmentEventOperator,
        isShipmentEvent: true,
      })
    }
  })

  defaultNodes.sort((a, b) => getSortPosition(a) - getSortPosition(b))

  return {
    nodes: defaultNodes,
  }
}

export const getRuleAction = (ruleAction: IIssueAutomationRuleActionEnum) => {
  if (ruleAction === IIssueAutomationRuleActionEnum.resolve) {
    return 'Close issue'
  }
  return 'Open issue'
}

export const getTimeWindowFieldLabel = (field: IIssueAutomationRuleNodeTimeWindowFieldTypeEnum) => {
  return TIME_WINDOW_FIELD_OPTIONS.find(item => item.value === field)?.label
}

export const getTimeWindowOperatorLabel = (
  operator: IIssueAutomationRuleNodeTimeWindowOperatorTypeEnum
) => {
  return TIME_WINDOW_OPERATOR_OPTIONS.find(item => item.value === operator)?.label.toLowerCase()
}

export const getTimeUnitLabel = (amount: number, unit: string) => {
  return amount > 1 ? unit : unit.slice(0, -1)
}

export const getNodesDescription = (rule: IIssueAutomationRuleFragment) => {
  return rule.issueAutomationRuleNodes
    .map(node => {
      if (node.parameters.__typename === 'IssueAutomationRuleNodeTimeWindowParameters') {
        const timeUnit = getUnitFromSeconds(node.parameters.timeWindowSeconds)
        const timeAmount = getTimeAmountFromSeconds(node.parameters.timeWindowSeconds, timeUnit)
        return `Time Window ${getTimeWindowOperatorLabel(
          node.parameters.timeWindowOperator
        )} ${timeAmount} ${getTimeUnitLabel(timeAmount, timeUnit)} from ${getTimeWindowFieldLabel(
          node.parameters.timeWindowField
        )}`
      }
      if (node.parameters.__typename === 'IssueAutomationRuleNodeShipmentStateParameters') {
        const shipmentStatusLabels = node.parameters.shipmentStates
          .map(state => SHIPMENT_STATUS_OPTIONS.find(item => item.value === state)?.label || '')
          .join(', ')
        return `Shipment Status is any of the following: ${shipmentStatusLabels}`
      }
      if (node.parameters.__typename === 'IssueAutomationRuleNodeShipmentInformationParameters') {
        const shipmentInformationOperatorIsTrue =
          node.parameters.shipmentInformationOperator ===
          IIssueAutomationRuleNodeShipmentInformationOperatorTypeEnum.is_true
        const shipmentInformationFields = node.parameters.shipmentInformationFields
          .map(
            field =>
              SHIPMENT_INFORMATION_FIELD_OPTIONS_M5.find(item => item.value === field)?.label || ''
          )
          .join(', ')
        return `Shipment is${
          shipmentInformationOperatorIsTrue ? ':' : ' not:'
        } ${shipmentInformationFields}`
      }
      if (node.parameters.__typename === 'IssueAutomationRuleNodeShipmentEventParameters') {
        // For now this node type only allows is true operator and one field, so will always have the same description
        return 'Pickup appointment date is same day as shipment status'
      }
      return ''
    })
    .join(' and ')
}

export const getRuleDescription = (rule: IIssueAutomationRuleFragment) => {
  const ruleAction = getRuleAction(rule.ruleAction)

  const nodesDescription = getNodesDescription(rule)

  return `${ruleAction} when ${nodesDescription}`
}

export const getIssueAutomationRuleNodes = (formData: TIssueRuleFormFormSchema) => {
  const newNodes: IIssueAutomationRuleNodeInput[] = compact<IIssueAutomationRuleNodeInput>(
    formData.nodes.map(node => {
      if (node.isShipmentStatus) {
        return {
          nodeType: IIssueAutomationRuleNodeTypeEnum.shipment_status,
          parameters: {
            shipmentStates: node?.shipmentStatusFields || [],
          },
        }
      }
      if (node.isTimeWindow) {
        const timeWindowSeconds = getTimeAmountInSeconds(
          node.timeWindowTimeAmount,
          node.timeWindowUnit
        )
        return {
          nodeType: IIssueAutomationRuleNodeTypeEnum.time_window,
          parameters: {
            timeWindowSeconds,
            timeWindowField: node.timeWindowField as IIssueAutomationRuleNodeTimeWindowFieldTypeEnum,
            timeWindowOperator: node.timeWindowOperator as IIssueAutomationRuleNodeTimeWindowOperatorTypeEnum,
          },
        }
      }
      if (node.isShipmentInformation) {
        return {
          nodeType: IIssueAutomationRuleNodeTypeEnum.shipment_information,
          parameters: {
            shipmentInformationFields: node.shipmentInformationFields.map(
              item => item as IIssueAutomationRuleNodeShipmentInformationFieldTypeEnum
            ),
            shipmentInformationOperator: node.shipmentInformationOperator as IIssueAutomationRuleNodeShipmentInformationOperatorTypeEnum,
          },
        }
      }
      if (node.isShipmentEvent) {
        return {
          nodeType: IIssueAutomationRuleNodeTypeEnum.shipment_event,
          parameters: {
            shipmentEventFields: [
              node.shipmentEventField as IIssueAutomationRuleNodeShipmentEventFieldTypeEnum,
            ],
            shipmentEventOperator: node.shipmentEventOperator as IIssueAutomationRuleNodeShipmentEventOperatorTypeEnum,
          },
        }
      }
      return null
    })
  )

  return newNodes
}

export const getSameDayMessage = (ruleNodes: IRuleNode[] | undefined) => {
  const shipmentStatusNodeIndex = ruleNodes?.findIndex(node => node.isShipmentStatus) ?? -1
  const shipmentEventNodeIndex = ruleNodes?.findIndex(node => node.isShipmentEvent) ?? -1

  if (shipmentStatusNodeIndex === -1 || shipmentEventNodeIndex === -1) {
    return ''
  }

  const hasTenderedStatus =
    ruleNodes?.[shipmentStatusNodeIndex].shipmentStatusFields?.some(
      (status: IShipmentStates) => status === IShipmentStates.tendered
    ) || false
  const hasOnHoldStatus =
    ruleNodes?.[shipmentStatusNodeIndex].shipmentStatusFields?.some(
      (status: IShipmentStates) => status === IShipmentStates.on_hold
    ) || false

  if (hasTenderedStatus && hasOnHoldStatus) {
    return SHIPMENT_EVENT_ON_HOLD_AND_TENDERED_STATUS_MESSAGE
  }
  if (hasOnHoldStatus) {
    return SHIPMENT_EVENT_ON_HOLD_STATUS_MESSAGE
  }
  if (hasTenderedStatus) {
    return SHIPMENT_EVENT_TENDERED_STATUS_MESSAGE
  }
  return ''
}
